import React, { useEffect, useState } from "react";
import { Button, Box, Container, Paper, Typography, IconButton, Chip } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { getOffers, deleteOffer } from "../services/offerService";
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import DescriptionIcon from '@mui/icons-material/Description';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

const ListOffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const response = await getOffers();
      
      // ✅ Handle different response structures
      let offersData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        offersData = response.payload;
      } else if (Array.isArray(response)) {
        offersData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        offersData = response.data;
      }
      
      // ✅ Map the data to include CustomerName from nested Customer object
      const mappedOffers = offersData.map(offer => ({
        ...offer,
        CustomerName: offer.Customer?.Name || offer.CustomerName || 'N/A',
        // ✅ Calculate TotalIncl if not present
        TotalIncl: offer.TotalIncl || offer.LineItems?.reduce((sum, item) => {
          const excl = item.TotalExcl || (item.Quantity * item.UnitPrice);
          const vat = (excl * item.VAT) / 100;
          return sum + excl + vat;
        }, 0) || 0
      }));
      
      setOffers(mappedOffers);
      console.log('✅ Offers loaded:', mappedOffers);
    } catch (error) {
      console.error('❌ Error fetching offers:', error);
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this offer?')) {
      try {
        await deleteOffer(id);
        setOffers((prev) => prev.filter((offer) => offer.Id !== id));
      } catch (error) {
        console.error('Error deleting offer:', error);
      }
    }
  };

  // ✅ Get status configuration
  const getStatusConfig = (statusValue) => {
    let status = 'Pending';
    let color = 'warning';
    
    if (typeof statusValue === 'number') {
      switch (statusValue) {
        case 1:
          status = 'Accepted';
          color = 'success';
          break;
        case 2:
          status = 'Rejected';
          color = 'error';
          break;
        case 0:
        default:
          status = 'Pending';
          color = 'warning';
          break;
      }
    } else if (typeof statusValue === 'string') {
      const lowerStatus = statusValue.toLowerCase();
      switch (lowerStatus) {
        case 'accepted':
          status = 'Accepted';
          color = 'success';
          break;
        case 'rejected':
          status = 'Rejected';
          color = 'error';
          break;
        case 'pending':
        default:
          status = 'Pending';
          color = 'warning';
          break;
      }
    }
    
    return { status, color };
  };

  const columns = [
    { field: "Id", headerName: "ID", flex: 0.5, minWidth: 80 },
    { field: "OfferNumber", headerName: "Offer #", flex: 1, minWidth: 120 },
    { field: "CustomerName", headerName: "Customer", flex: 1.5, minWidth: 180 },
    { 
      field: "Status", 
      headerName: "Status", 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const { status, color } = getStatusConfig(params.row.Status);
        
        return (
          <Chip
            label={status}
            color={color}
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.75rem',
              height: '24px',
              minWidth: '80px'
            }}
          />
        );
      }
    },
    { 
      field: "TotalIncl", 
      headerName: "Total", 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          €{Number(params.row.TotalIncl || 0).toFixed(2)}
        </Typography>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/sales/offers/${params.row.Id}`)}
            sx={{
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
              },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.Id)}
            sx={{
              color: '#ef4444',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Offers / Proposals
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your offers and proposals
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/sales/offers/add')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.2s',
              },
            }}
          >
            Add Offer
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <ReusableDataGrid
          rows={offers}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
          checkboxSelection
        />
      </Paper>
    </Container>
  );
};

export default ListOffersPage;